/**
 * Sala do dia (RECEP-01): em que sala cada agenda atende num dia.
 *
 * Mesma regra do backend (`rooms/ResourceDayRooms.php`): vale a faixa do dia com o
 * maior horário de início menor ou igual ao horário pedido (roomId null = "sem sala");
 * sem faixa, a sala padrão da agenda; sem sala padrão, nenhuma. Cada agendamento
 * continua com o seu próprio roomId; isto só diz o que pré-preencher.
 */

const DAY_START = '00:00';

/** Data local como YYYY-MM-DD (sem passar pelo UTC do toISOString). */
export function toDayKey(date) {
  const d = date instanceof Date ? date : new Date(date);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/** Horário HH:MM de um Date (ou de hora e minuto soltos). */
export function toTimeKey(hour, minute = 0) {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

/** Faixas de uma agenda no dia, em ordem de horário. */
export function bandsFor(assignments, resourceId) {
  return (assignments || [])
    .filter((a) => String(a.resourceId) === String(resourceId))
    .sort((a, b) => (a.startTime < b.startTime ? -1 : a.startTime > b.startTime ? 1 : 0));
}

/**
 * Sala de uma agenda num horário do dia.
 * @returns {{ roomId: string, source: 'day'|'default'|'none', startTime: string }}
 *   roomId '' = sem sala; startTime = início da faixa que vale (00:00 quando vem do padrão)
 */
export function resolveRoomFor(assignments, resource, time = '23:59') {
  const bands = bandsFor(assignments, resource?.Id).filter((b) => b.startTime <= time);
  if (bands.length > 0) {
    const band = bands[bands.length - 1];
    return { roomId: band.roomId != null ? String(band.roomId) : '', source: 'day', startTime: band.startTime };
  }
  if (resource?.DefaultRoomId) {
    return { roomId: String(resource.DefaultRoomId), source: 'default', startTime: DAY_START };
  }
  return { roomId: '', source: 'none', startTime: DAY_START };
}

/**
 * Resumo de cada agenda para o card e para o chip da coluna: a sala que vale no
 * fim do dia (a última faixa), com o rótulo pronto.
 */
export function summarizeDayRooms(assignments, resources, rooms) {
  const labelOf = (roomId) => (rooms || []).find((r) => String(r.Id) === String(roomId))?.Label || '';
  return (resources || []).map((resource) => {
    const resolved = resolveRoomFor(assignments, resource);
    return {
      resourceId: String(resource.Id),
      resourceLabel: resource.Label,
      roomId: resolved.roomId,
      roomLabel: resolved.roomId ? labelOf(resolved.roomId) : '',
      source: resolved.source,
      startTime: resolved.startTime,
      fromTime: resolved.startTime !== DAY_START ? resolved.startTime : '',
    };
  });
}

/**
 * Salas de uma pessoa só (capacidade 1) que duas ou mais agendas usam no mesmo dia.
 * @returns {Array<{ roomLabel: string, resourceLabels: string[] }>}
 */
export function findRoomConflicts(summaries, rooms) {
  const byRoom = new Map();
  summaries.forEach((s) => {
    if (!s.roomId) return;
    const room = (rooms || []).find((r) => String(r.Id) === s.roomId);
    if (!room || Number(room.Capacity || 1) > 1) return;
    if (!byRoom.has(s.roomId)) byRoom.set(s.roomId, { roomLabel: room.Label, resourceLabels: [] });
    byRoom.get(s.roomId).resourceLabels.push(s.resourceLabel);
  });
  return [...byRoom.values()].filter((c) => c.resourceLabels.length > 1);
}

/** "14h" ou "14h30", para chips curtos. */
export function shortTime(time) {
  if (!time) return '';
  const [h, m] = time.split(':');
  return m === '00' ? `${Number(h)}h` : `${Number(h)}h${m}`;
}

/** Frase da prévia: o que muda ao aplicar a sala a partir de um horário. */
export function previewSentence({ toChange, kept }, roomLabel, startTime) {
  const destino = roomLabel ? `para ${roomLabel}` : 'para "sem sala"';
  const plural = (n, s, p) => `${n} ${n === 1 ? s : p}`;
  const fromStart = !startTime || startTime === DAY_START;
  if (toChange === 0) {
    return fromStart
      ? 'Nenhum agendamento muda de sala. Os próximos já nascem nessa sala.'
      : `Nenhum agendamento a partir das ${startTime} muda de sala. Os próximos já nascem nessa sala.`;
  }
  if (fromStart) {
    return `${plural(toChange, 'agendamento passa', 'agendamentos passam')} ${destino}.`;
  }
  const antes = kept > 0 ? ` Os ${kept} de antes ficam como estão.` : '';
  return `${plural(toChange, 'agendamento a partir das', 'agendamentos a partir das')} ${startTime} ${toChange === 1 ? 'passa' : 'passam'} ${destino}.${antes}`;
}
